import { format } from "date-fns";

import { copyToClipboard } from "@/components/helpers/browser/copy-to-clipboard";
import { _truncateFilename } from "@/components/helpers/string-utils";
import { RoleplaySessionTranscriptFragment } from "@/graphql";

/**
 * Formats time in seconds to SRT timestamp format (HH:MM:SS,MS)
 * @param timeInSeconds - Time in seconds to format
 * @returns Formatted time string in SRT format
 */
export function formatSrtTimestamp(timeInSeconds: number): string {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  const milliseconds = Math.floor((timeInSeconds % 1) * 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")},${milliseconds
    .toString()
    .padStart(3, "0")}`;
}

/**
 * Converts transcript messages to SRT format
 * @param messages - Transcript messages to convert
 * @returns String in SRT format
 */
export function convertToSrt(
  messages: Array<
    RoleplaySessionTranscriptFragment & {
      sender?: { name: string };
      secondsFromStart?: number | null;
      nextMessageStart?: number | null;
    }
  >,
): string {
  return messages
    .map((message, index) => {
      if (
        !message.text ||
        message.secondsFromStart === undefined ||
        message.secondsFromStart === null
      )
        return "";

      // Calculate end time - use next message start time or add default duration
      const startTime = message.secondsFromStart;
      let endTimeValue = 0;

      if (message.nextMessageStart) {
        endTimeValue = message.nextMessageStart;
      } else if (
        index < messages.length - 1 &&
        messages[index + 1].secondsFromStart !== undefined &&
        messages[index + 1].secondsFromStart !== null
      ) {
        endTimeValue = messages[index + 1].secondsFromStart || 0;
      } else {
        endTimeValue = startTime + 5; // Default 5 seconds if no duration info
      }
      const sender = message.sender;

      return `${index + 1}
${formatSrtTimestamp(startTime)} --> ${formatSrtTimestamp(endTimeValue)}
${sender?.name}: ${message.text}
`;
    })
    .join("\n");
}

/**
 * Converts transcript messages to plain text format
 * @param messages - Transcript messages to convert
 * @returns String in plain text format
 */
export function convertToPlainText(
  messages: Array<
    RoleplaySessionTranscriptFragment & { sender?: { name: string } }
  >,
): string {
  return messages
    .map((message) => {
      if (!message.text) return "";

      const sender = message.sender;

      const timestamp =
        message.secondsFromStart !== undefined &&
        message.secondsFromStart !== null
          ? `[${formatTimestamp(message.secondsFromStart)}]`
          : "";

      return `${timestamp} ${sender?.name}: ${message.text}`;
    })
    .join("\n<br />");
}

/**
 * Formats time in seconds to readable timestamp (MM:SS)
 * @param timeInSeconds - Time in seconds to format
 * @returns Formatted time string
 */
function formatTimestamp(timeInSeconds: number): string {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Copies transcript to clipboard
 * @param messages - Transcript messages to copy
 * @returns Promise that resolves when copying is complete
 */
export async function copyTranscriptToClipboard(
  messages: Array<
    RoleplaySessionTranscriptFragment & { sender?: { name: string } }
  >,
): Promise<void> {
  const plainText = convertToPlainText(messages);
  await copyToClipboard(plainText);
}

/**
 * Downloads transcript as SRT file
 * @param messages - Transcript messages to download
 * @param userName - User's name for filename
 * @param scenarioName - Scenario name for filename
 */
export function downloadTranscriptAsSrt(
  messages: RoleplaySessionTranscriptFragment[],
  userName: string,
  scenarioName: string,
): void {
  const srtContent = convertToSrt(messages);
  const formattedDate = format(new Date(), "yyyy-MM-dd");

  // Clean up names for filename
  const cleanUserName = userName?.replace(/[^a-zA-Z0-9]/g, "_") || "User";
  const truncatedScenarioName = _truncateFilename(scenarioName);
  const cleanScenarioName =
    truncatedScenarioName?.replace(/[^a-zA-Z0-9]/g, "_") || "scenario";

  const filename = `${cleanUserName}_${cleanScenarioName}_${formattedDate}_Transcript.srt`;

  // Create and download the file
  const blob = new Blob([srtContent], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
